import { useEffect, useState } from 'react'
import { FloatingLabel, Form, Spinner, Table } from 'react-bootstrap';
import { PermissionModel, RoleModel } from 'model/roles-permission-model';
import { useFormik } from 'formik';
import { editRoleSchema, roleSchema } from 'validation-schema/user-schema';
import { useAddRoleMutation, useGetPermissionsByIdQuery, useUpdateRoleMutation } from 'api/userManagementApi';
import { showToastError, showToastSuccess } from 'components/toastify/ToastNotifications';
import { FormattedDate } from 'components/formatted-date';

type Props = {
    activeModalTab: string;
    handleHideModal: Function;
    permissions: PermissionModel[];
    refetchRoles: Function; 
    modalRoleData: RoleModel | undefined;
}

interface GroupedPermissions {
    [category: string]: PermissionModel[];
}

const RoleModalTabs = ({ activeModalTab, handleHideModal, permissions, refetchRoles, modalRoleData }: Props) => {

    const [activeTab, setActiveTab] = useState(activeModalTab);
    const [loading, setLoading] = useState(false);

    const { data: permissionsData, refetch: refetchPermission } = useGetPermissionsByIdQuery(modalRoleData?.id || '');
    useEffect (()=> {
        refetchPermission();
    }, []);

    const [createRole, createRoleResult] = useAddRoleMutation();

    const formik = useFormik({
        initialValues: {
            roleName: '',
            permissionIds: [''], // Assuming you want to initialize it with an empty string
        },
        validationSchema: roleSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const filteredPermissionIds = values.permissionIds.filter(id => id !== "");
                const model = {
                    roleName: values.roleName,
                    permissionIds: filteredPermissionIds
                };
                const response = await createRole(model).unwrap();
                if (response) {
                    showToastSuccess(response?.ResponseMessage);
                    handleHideModal()
                    refetchRoles();
                }
            }
            catch (error: any) {
                showToastError(error?.data.ResponseMessage)
            }
            finally {
                setLoading(false);
            }
        },
    });

    const [updateRole, updateRoleResult] = useUpdateRoleMutation();

    const update = useFormik({
        initialValues: {
            roleId: modalRoleData?.id || '',
            permissionIds: [''], // Assuming you want to initialize it with an empty string
        },
        validationSchema: editRoleSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const filteredPermissionIds = values.permissionIds.filter(id => id !== "");
                const model = {
                    roleId: values.roleId,
                    permissionIds: filteredPermissionIds
                };
                const response = await updateRole(model).unwrap();
                if (response) {
                    showToastSuccess(response?.ResponseMessage);
                    handleHideModal()
                    refetchRoles();
                }
            }
            catch (error: any) {
                showToastError(error?.data.ResponseMessage)
            }
            finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (permissionsData?.data?.permissions) {
            const filteredIds = permissions?.filter(item => permissionsData?.data?.permissions.includes(item.description)).map(item => item.id);
          update.setFieldValue('permissionIds', filteredIds);
        }
      }, [permissionsData]);

    const groupPermissionsByCategory = (permissions: PermissionModel[]): GroupedPermissions => {
        return permissions.reduce((grouped, permission) => {
            const { category } = permission;
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(permission);
            return grouped;
        }, {} as GroupedPermissions);
    };

    const groupedPermissions = groupPermissionsByCategory(permissions);

    return (
        <div>
            {activeTab === 'add-role' && <div>
                <h5 className='fw-bold mb-4 px-1'>Add role</h5>
                <Form onSubmit={formik.handleSubmit}>
                    <FloatingLabel controlId="floatingInput" label="Enter Role" className="float-input my-4" >
                        <Form.Control name="roleName" value={formik.values.roleName} onChange={formik.handleChange} onBlur={formik.handleBlur} type="text" placeholder="IT Support Admin" />
                        {formik.touched.roleName && formik.errors.roleName && <span className='text-danger font-size-sm'>{formik.errors.roleName}</span>}
                    </FloatingLabel>
                    <h6 className='fw-bold my-4 mx-1'>Permissions</h6>
                    <div className='row overflow-y-scroll ms-1 ps-2' style={{ height: "40vh" }}>

                        {Object.entries(groupedPermissions)?.map(([category, permissions]) => (
                            <div className='col-lg-6 px-0' key={category}>
                                <p className='pt-4'>{category}</p>
                                {permissions.map(permission => (
                                    <Form.Check key={permission?.id} value={permission?.id} onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const updatedPermissionIds = isChecked
                                          ? [...formik.values.permissionIds, permission?.id ?? '']
                                          : formik.values.permissionIds.filter((id: string) => id !== permission?.id);
                                        formik.setFieldValue('permissionIds', updatedPermissionIds);
                                      }} label={permission?.description} id={permission?.description} checked={formik.values.permissionIds.includes(permission?.id)} className="dark-text my-2" />
                                ))}
                            </div>
                        ))}
                    </div>
                    {formik.errors.permissionIds && <span className='text-danger'>{formik.errors.permissionIds}</span>}
                    <button disabled={loading} className='btn-primary w-100 rounded-5 my-4 mx-2'>Add role {loading && <Spinner size='sm'/>}</button>
                </Form>
            </div>}

            {activeTab === 'edit-role' && <div>
                <h5 className='fw-bold my-4 px-1'>Edit role</h5>
                <Form onSubmit={update.handleSubmit}>
                <FloatingLabel controlId="floatingInput" label="Enter Role" className="float-input my-4" >
                        <Form.Control disabled name="roleName" value={modalRoleData?.name || ''} type="text" placeholder="IT Support Admin" />
                    </FloatingLabel>

                    <h6 className='fw-bold my-4 mx-1'>Permissions</h6>
                    <div className='row overflow-y-scroll ms-1' style={{ height: "400px" }}>
                    {Object.entries(groupedPermissions)?.map(([category, permissions]) => (
                            <div className='col-lg-6 px-0' key={category}>
                                <p className='pt-4'>{category}</p>
                                {permissions.map(permission => (
                                    <Form.Check key={permission?.id} value={permission?.id} onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const updatedPermissionIds = isChecked
                                          ? [...update.values.permissionIds, permission?.id ?? '']
                                          : update.values.permissionIds.filter((id: string) => id !== permission?.id);
                                        update.setFieldValue('permissionIds', updatedPermissionIds);
                                      }} label={permission?.description} id={permission?.description} checked={update.values.permissionIds.includes(permission?.id)} className="dark-text my-2" />
                                ))}
                            </div>
                        ))}
                    </div>
                    <button disabled={loading} className='btn-primary w-100 rounded-5 my-4 mx-2'>Update role {loading && <Spinner size='sm'/>}</button>
                </Form>
            </div>}

            {activeTab === 'viewUsers' && <div>
                <h5 className='fw-bold mb-4 px-1'>{modalRoleData?.name} Users</h5>
                <Table responsive className='table font-size-sm'>
              <thead>
                <tr>
                  <th>
                    <label className='fw-normal'>SN</label>
                  </th>
                  <th>
                    <label className='fw-normal'>Name</label>
                  </th>
                  <th>
                    <label className='fw-normal'>Email</label>
                  </th>
                  <th>
                    <label className='fw-normal'>Date Created</label>
                  </th>
                </tr>
              </thead>
              <tbody>
              {modalRoleData?.users?.map((item, key) => <tr key={key} className='align-middle'>
                  <td>{key + 1}</td>
                  <td><span className='text-secondary'>{item?.lastName} {item?.firstName}</span></td>
                  <td><span className='text-secondary'>{item?.email}</span></td>
                  <td><span className='text-secondary'>{FormattedDate(item?.dateCreatedInEpochMilliseconds)}</span></td>
                </tr>)}
              </tbody>
            </Table>
            </div>}
        </div>
    )
}

export default RoleModalTabs;
